/** @jsxImportSource @emotion/react */
import * as React from "react";
import { MemberCardType } from "./models/MemberCardType";
import { UserTermListItem } from "../../service/api-response/UserTermListSuccessResponse";
import { useTranslation } from "react-i18next";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { FormRow, IMAGE_GUIDE_LINK } from "../common/hook-form";
import { PrimaryButton } from "../../components/Buttons";
import { SpinnerBlock } from "../common/Spinner";
import { FormProvider, useController, useForm } from "react-hook-form";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { ColorPickerInput } from "../../components/forms/inputs/ColorPickerInput";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";
import { css } from "@emotion/react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { ExternalLinkButton } from "../../components/ExternalLinkButton";
import { ButtonAction } from "../../components/NewButtons";
import { SwitchInput } from "../../components/forms/inputs/SwitchInput";
import { CheckboxField } from "../../components/forms/fields/CheckboxField";
import { CollectionMethod } from "../shop/model/ShopSuccessResponse";
import { getCollectionMethodLabel } from "../../util";

interface MemberCardTypeData extends MemberCardType {
  tagsHash: {
    [key: string]: boolean;
  };
}

const validationSchema = yup.object().shape({
  name: yup.string().required("Please enter name to continue"),
  memberCardTypeDiscount: yup.object({
    value: yup
      .number()
      .typeError("Enter discount percentage to continue")
      .min(0, "Cannot be below 0")
      .required("Enter discount percentage to continue"),
  }),
  tagLine: yup.string().required("Please enter tag line to continue"),
  backgroundColor: yup
    .string()
    .required("Please choose background color to continue"),
  onlinePosGroup: yup.string().required("Enter online pos group to continue"),
  onlinePosCampaignGroup: yup
    .string()
    .required("Enter online pos campaign group to continue"),
});

const checkboxesStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const inputWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const imageWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const imageHeaderWrapperStyle = css`
  display: flex;
  flex-direction: column;
  align-items: start;
`;

type Props = {
  memberCardType: MemberCardType | Partial<MemberCardType>;
  onSubmit: (
    values: any,
    backgroundFile: File | null,
    logoFile: File | null
  ) => void;
  userTerms: UserTermListItem[];
  isUpsertingMemberCardType: boolean | undefined;
};

export const MemberCardTypeUpsertForm = ({
  memberCardType,
  onSubmit,
  isUpsertingMemberCardType,
  userTerms,
}: Props) => {
  const { t } = useTranslation();
  const [logo, setLogo] = React.useState<string>("");
  const [background, setBackground] = React.useState<string>("");
  const [logoUploadRef, setLogoUploadRef] = React.useState<any>();
  const [backgroundUploadRef, setBackgroundUploadRef] = React.useState<any>();
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = React.useState<File | null>(null);

  let membercardTypeDiscount: Partial<
    MemberCardTypeData["memberCardTypeDiscount"]
  > = {
    appliesToClassifications: ["generic", "production_addition"],
    appliesToCollectionMethods: [],
  };

  if (
    memberCardType?.memberCardTypeDiscount &&
    // Parser from backend sends [] if it's empty
    !Array.isArray(memberCardType.memberCardTypeDiscount)
  ) {
    membercardTypeDiscount = {
      ...memberCardType.memberCardTypeDiscount,
      appliesToCollectionMethods:
        memberCardType.memberCardTypeDiscount?.appliesToCollectionMethods || [],
    };
  }

  const methods = useForm<
    MemberCardTypeData & {
      limitOrderTypes: boolean;
    }
  >({
    defaultValues: {
      ...memberCardType,
      sortKey: memberCardType.sortKey || 2,
      allowChallengeProgress: memberCardType.allowChallengeProgress,
      limitOrderTypes:
        memberCardType.memberCardTypeDiscount?.appliesToCollectionMethods ===
          null ||
        memberCardType.memberCardTypeDiscount?.appliesToCollectionMethods ===
          undefined
          ? false
          : true,
      showBenefits: memberCardType.showBenefits || false,
      visible: memberCardType.visible || false,
      disabled: memberCardType.disabled || false,
      showTripleUp: memberCardType.showTripleUp || false,
      memberCardTypeDiscount: membercardTypeDiscount,
    },
    resolver: yupResolver(validationSchema),
  });

  const { field } = useController({
    control: methods.control,
    name: "memberCardTypeDiscount.appliesToCollectionMethods",
  });

  React.useEffect(() => {
    if (!memberCardType) {
      return;
    }
    if (memberCardType.logoUrl) {
      setLogo(memberCardType.logoUrl);
    }
    if (memberCardType.backgroundImageUrl) {
      setBackground(memberCardType.backgroundImageUrl);
    }
  }, [memberCardType, methods]);

  const handleSubmit = (values: MemberCardTypeData) => {
    if (values.userTermId === "") {
      values.userTermId = null;
    }
    const v = {
      ...values,
      planId: values.planId ? parseInt(values.planId, 10) : null,
      memberCardTypeDiscount: {
        ...values.memberCardTypeDiscount,
        type: "DISCOUNT_TYPE_PERCENTAGE_ORDER",
        appliesToClassifications: Array.from(
          new Set([
            ...values.memberCardTypeDiscount.appliesToClassifications,
            "generic",
            "production_addition",
          ])
        ),
      },
    };

    onSubmit(v, backgroundFile, logoFile);
  };

  const onLogoUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    logoUploadRef.click();
  };

  const onBackgroundUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    backgroundUploadRef.click();
  };

  const onLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    setLogoFile(file);

    const previewUrl = URL.createObjectURL(file);
    setLogo(previewUrl);
  };

  const onBackgroundFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    setBackgroundFile(file);

    const previewUrl = URL.createObjectURL(file);
    setBackground(previewUrl);
  };

  const userTermOptions =
    userTerms?.map((x) => ({
      label: x.title,
      value: x.id,
    })) || [];

  userTermOptions.unshift({
    label: "None",
    value: "",
  });

  if (!memberCardType) {
    return <SpinnerBlock />;
  }

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <form className="card">
        <div className="card-header">
          <StyledText
            color={colors.textPrimary}
            className="bodyHeaderOneRegular card-title"
          >
            {memberCardType.id
              ? "Edit membercard template template"
              : "New membercard template template"}
          </StyledText>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2"
                >
                  Standard membercard template information
                </StyledText>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mb-4"
                >
                  Fill out the information below to create your membercard
                  template. OBS: to activate the membercard template you need to
                  press the Menu tab: “Partnerships” and activate the membercard
                  template.
                </StyledText>
              </div>
              <FormRow>
                <TextInput
                  name="name"
                  label="Name"
                  helperText="Write a short description of the membercard template here. The description will be shown under the membercard template in the app. "
                />
                <TextInput
                  name="memberCardTypeDiscount.value"
                  label="Discount Percentage"
                  placeholder="0"
                  min={0}
                  floatingElement={
                    <FontAwesomeIcon
                      color={colors.textPrimary}
                      icon={solid("percentage")}
                    />
                  }
                  type="number"
                  helperText="Type in the percentage discount this membercard template gives from 1-100. Do not include %. Eg. for 50% discount, write “50”."
                />
              </FormRow>
              <div className="mt-6">
                <div css={imageWrapperStyle}>
                  <div css={imageHeaderWrapperStyle}>
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyHeaderOneSemi mb-2"
                    >
                      Membercard template design
                    </StyledText>
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyTextRegular mb-4"
                    >
                      Upload your Membercard template design here.
                    </StyledText>
                  </div>
                  <ExternalLinkButton
                    width="fit-content"
                    textColor={colors.textPrimary}
                    href={IMAGE_GUIDE_LINK}
                    backgroundColor={colors.transparent}
                    hoverColor={colors.ctaLight}
                    text="Image guide"
                    icon={
                      <FontAwesomeIcon
                        icon={solid("arrow-up-right-from-square")}
                      />
                    }
                  />
                </div>
              </div>
              <div className="row">
                {background ? (
                  <div className="col-md-3">
                    <div className="card">
                      <>
                        <a href={background}>
                          <img src={background} alt="" />
                        </a>
                        <div className="card-footer p-2">
                          <div className="caption text-right">
                            <PrimaryButton
                              backgroundColor={colors.white}
                              hoverColor={colors.ctaLight}
                              width="100%"
                              color={colors.textPrimary}
                              onClick={(e) => onBackgroundUpload(e)}
                            >
                              Change
                            </PrimaryButton>
                          </div>
                        </div>
                      </>
                    </div>
                    <input
                      className="form-control-file"
                      name={`backgroundUrl`}
                      ref={(ref) => setBackgroundUploadRef(ref)}
                      type="file"
                      style={{ display: "none" }}
                      accept=".jpg,.jpeg,.png"
                      onChange={onBackgroundFileSelect}
                    />
                  </div>
                ) : (
                  <div className="col-12 mb-1">
                    <input
                      className="form-control-file"
                      name={`backgroundUrl`}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={onBackgroundFileSelect}
                    />
                  </div>
                )}
              </div>
              <div className="mt-2">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2"
                >
                  Membercard template configuration
                </StyledText>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mb-4"
                >
                  Configure your membercard template below. Choose membercard
                  template tag line text and the colour of the tag line text.
                  This text will appear in the bottom of the membercard template
                  in the app. Additionally choose if the membercard template
                  should be visible in app and if it is disabled.
                </StyledText>
              </div>
              <FormRow>
                <TextInput
                  name="tagLine"
                  label="Tag Line text"
                  helperText="Tag line of the membercard template type."
                />
                <ColorPickerInput
                  label="Tag line text colour"
                  name="textColor"
                  helperText="The text color of the membercard template tag line."
                />
              </FormRow>
              <div css={checkboxesStyle}>
                <CheckboxInput
                  label={`${t("member_card_type_upsert_form.visible")}`}
                  name="visible"
                />
                <CheckboxInput
                  label={`${t("member_card_type_upsert_form.disabled")}`}
                  name="disabled"
                />
              </div>
              <div className="mb-4 mt-6">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2"
                >
                  Rewards & Challenges
                </StyledText>
              </div>
              <FormRow className="mt-2">
                <SwitchInput
                  label="Customer earns points for Challenges and Rewards when making a purchase where membercard is added. Example: If a membercard gives 25% discount on 100 DKK purchase, the customer pays 75 DKK and will earn points based on the 75 DKK they pay. "
                  name="allowChallengeProgress"
                />
              </FormRow>
              <div className="mt-6">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2"
                >
                  OnlinePOS configuration
                </StyledText>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mb-4"
                >
                  Set up the integration with OnlinePOS below.
                </StyledText>
              </div>
              <FormRow>
                <TextInput
                  name="onlinePosGroup"
                  label="OnlinePOS group"
                  helperText="Write the OnlinePOS group here. Example: (COMPANY NAME) - (NAME OF THE membercard template)."
                />
                <TextInput
                  name="onlinePosCampaignGroup"
                  label="OnlinePOS campaign group"
                  helperText="Write the OnlinePOS campaign group here. Example: (COMPANY NAME) - (NAME OF THE membercard template)."
                />
              </FormRow>
              <div className="mt-6">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2"
                >
                  Advanced membercard template information
                </StyledText>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mb-4"
                >
                  Below is the advanced membercard template information. These
                  are not required in order to create a membercard template.
                </StyledText>
              </div>
              <div css={inputWrapperStyle}>
                <FormRow>
                  <TextInput
                    name="planId"
                    label="Plan id"
                    helperText="Id of the associated plan."
                  />
                  <TextInput
                    name="backgroundSubtitle"
                    label="Background subtitle"
                  />
                  <SelectInput
                    label="User term"
                    name="userTermId"
                    options={userTermOptions}
                  />
                </FormRow>
                <ColorPickerInput
                  label="Text colour"
                  name="textColor"
                  helperText="The text color of the membercard template type."
                />
                <ColorPickerInput
                  label="Background color"
                  name="backgroundColor"
                  helperText="The background color of the membercard template type."
                />
                <CheckboxField
                  label="The discount can be used for delivery fee. If checked, then users who use membercards created from this membercard template, can use the discount for delivery fee."
                  checked={
                    !!methods
                      .watch("memberCardTypeDiscount.appliesToClassifications")
                      .includes("delivery")
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const currentValues = methods.getValues(
                      "memberCardTypeDiscount.appliesToClassifications"
                    );
                    if (checked) {
                      methods.setValue(
                        "memberCardTypeDiscount.appliesToClassifications",
                        [...currentValues, "delivery"]
                      );
                    } else {
                      methods.setValue(
                        "memberCardTypeDiscount.appliesToClassifications",
                        currentValues.filter((v) => v !== "delivery")
                      );
                    }
                  }}
                />
                <CheckboxField
                  label='The discount can be used for "fee for small order". If checked, then users who use membercards created from this membercard template, can use the discount for "fee for small order".'
                  checked={
                    !!methods
                      .watch("memberCardTypeDiscount.appliesToClassifications")
                      .includes("delivery_buffer")
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const currentValues = methods.getValues(
                      "memberCardTypeDiscount.appliesToClassifications"
                    );
                    if (checked) {
                      methods.setValue(
                        "memberCardTypeDiscount.appliesToClassifications",
                        [...currentValues, "delivery_buffer"]
                      );
                    } else {
                      methods.setValue(
                        "memberCardTypeDiscount.appliesToClassifications",
                        currentValues.filter((v) => v !== "delivery_buffer")
                      );
                    }
                  }}
                />
              </div>
              <div>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi mb-2 mt-6"
                >
                  Order types
                </StyledText>
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mb-4"
                >
                  You can limit the member card template to only work for
                  specific order types. To limit the member card template to
                  specific order types, check the toggle below and select the
                  appropriate order types. Fx. if you want to limit the member
                  card template to only work for pick-up orders, select pick-up
                  below. If you want to allow all order types, leave toggle
                  below unchecked.
                </StyledText>
              </div>
              <FormRow className="mb-4">
                <SwitchInput
                  label="Limit voucher template to specific order types"
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked) {
                      return;
                    }

                    field.onChange([]);
                  }}
                  name="limitOrderTypes"
                />
              </FormRow>
              {methods.watch("limitOrderTypes") && (
                <>
                  {Object.values(CollectionMethod).map((key, index) => (
                    <FormRow className="mb-2" key={index}>
                      <CheckboxField
                        label={getCollectionMethodLabel(key)}
                        value={key}
                        checked={
                          field.value?.includes(key as CollectionMethod) ||
                          false
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            field.onChange([...(field.value || []), key]);
                          } else {
                            field.onChange(
                              field.value?.filter((v) => v !== key) || []
                            );
                          }
                        }}
                      />
                    </FormRow>
                  ))}
                </>
              )}
              <label className="form-label mt-2">Logo</label>
              <div className="row">
                {logo ? (
                  <div className="col-md-3">
                    <div className="card">
                      <>
                        <a href={logo}>
                          <img src={logo} alt="" />
                        </a>
                        <div className="card-footer p-2">
                          <div className="caption text-right">
                            <PrimaryButton
                              backgroundColor={colors.white}
                              hoverColor={colors.ctaLight}
                              width="100%"
                              color={colors.textPrimary}
                              onClick={(e) => onLogoUpload(e)}
                            >
                              Change
                            </PrimaryButton>
                          </div>
                        </div>
                      </>
                    </div>
                    <input
                      className="form-control-file"
                      name={`logoUrl`}
                      ref={(ref) => setLogoUploadRef(ref)}
                      type="file"
                      style={{ display: "none" }}
                      accept=".jpg,.jpeg,.png"
                      onChange={onLogoFileSelect}
                    />
                  </div>
                ) : (
                  <div className="col-12 mb-1">
                    <input
                      className="form-control-file"
                      name={`logoUrl`}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={onLogoFileSelect}
                    />
                  </div>
                )}
              </div>
              {/* <div>
                    <div className="form-label">
                      {t("member_card_type_upsert_form.show_triple_up")}
                    </div>
                    <label className="custom-switch">
                      <Checkbox
                        field="showTripleUp"
                        className="custom-switch-input"
                      />
                      <span className="custom-switch-indicator" />
                    </label>
                  </div>
                  <div>
                    <div className="form-label">
                      {t("member_card_type_upsert_form.show_benefits")}
                    </div>
                    <label className="custom-switch">
                      <Checkbox
                        field="showBenefits"
                        className="custom-switch-input"
                      />
                      <span className="custom-switch-indicator" />
                    </label>
                  </div> */}
            </div>
          </div>
        </div>
        <div className="card-footer d-flex justify-content-end">
          <ButtonAction
            type="button"
            disabled={isUpsertingMemberCardType}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            {isUpsertingMemberCardType ? (
              <>
                {t(
                  "member_card_type_upsert_form.submit_button.label_is_saving"
                )}
              </>
            ) : (
              <>{t("member_card_type_upsert_form.submit_button.label")}</>
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
